import { Formik, FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, useWindowDimensions, View } from 'react-native';
import styled from 'styled-components/native';
import * as Yup from 'yup';
import Form from '../../components/form/Form';
import FormButton from '../../components/form/FormButton';
import FormInput from '../../components/form/FormInput';
import Spacer from '../../components/Spacer';
import { H2, P } from '../../components/Typography';
import {
  PasswordRecoverRequest,
  usePasswordRecover
} from '../../hooks/user/usePasswordRecover';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Link from '../../components/Link';
import { IntroParamList } from '../../navigation/IntroNavigator';
import accessibilityTags from '../../../accessibilityTags';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';

const StyledView = styled(KeyboardAvoidingView)<{ isDesktop: boolean }>`
  flex: 1;
  align-items: center;
  ${p => (p.isDesktop ? 'justify-content: center;' : '')}
`;

const StyledImageBackground = styled.ImageBackground`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const StyledForgotPasswordView = styled.ScrollView<{ isDesktop: boolean }>`
  width: 100%;
  height: 100%;
  padding: ${p => (p.isDesktop ? 0 : 87)}px 24px 40px 24px;
  ${p => (p.isDesktop ? 'max-height: 1080px;' : '')}
`;

const StyledLink = styled(Link)`
  text-align: center;
`;

const StyledFormView = styled.View<{ isDesktop: boolean }>`
  flex: 1;
  justify-content: space-between;
  align-items: ${p => (p.isDesktop ? 'center' : 'flex-start')};
  align-self: center;
  max-width: 438px;
  width: 100%;
`;

const StyledHeaderView = styled.View<{ isDesktop: boolean }>`
  align-items: ${p => (p.isDesktop ? 'center' : 'flex-start')};
  align-self: center;
  max-width: 438px;
  width: 100%;
  margin-top: ${p => (p.isDesktop ? '100' : '0')}px;
`;

const ForgotPasswordScreen: React.FC = () => {
  const window = useWindowDimensions();
  const isDesktop = window.width >= 768;
  const formikRef = useRef<FormikProps<any> | null>();
  const navigation = useNavigation<StackNavigationProp<IntroParamList>>();
  const [emailSent, setEmailSent] = useState<string>('');

  const { success, isLoading, doPasswordRecover } = usePasswordRecover();

  const handleSubmit = (form: PasswordRecoverRequest) => {
    setEmailSent(form.email);
    doPasswordRecover(form.email);
  };

  useEffect(() => {
    if (!isLoading && success) {
      formikRef.current?.resetForm();
      navigation.navigate('ResetPasswordSentScreen', { email: emailSent });
    }
  }, [success, isLoading]);

  formikRef.current?.setSubmitting(isLoading);
  return (
    <StyledView behavior='padding' isDesktop={isDesktop}>
      <StyledImageBackground
        resizeMode='stretch'
        source={require('../../assets/intro/background.png')}
      />
      <StyledForgotPasswordView
        isDesktop={isDesktop}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <StyledHeaderView isDesktop={isDesktop}>
          <H2
            color='dark'
            accessibilityLabel={accessibilityTags.forgotPasswordTitleID}
            testID={accessibilityTags.forgotPasswordTitleID}
            padding={2}
          >
            Forgot Password
          </H2>
          <P color='dark' center={isDesktop}>
            Enter the email associated with your account and we’ll send you a
            link to reset your password.
          </P>
        </StyledHeaderView>
        <Spacer size={5} />
        <StyledFormView isDesktop={isDesktop}>
          <Formik
            validateOnBlur={false}
            innerRef={formik => (formikRef.current = formik)}
            initialValues={{
              email: ''
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string().email().required('Email is required.')
            })}
            onSubmit={formValues => {
              handleSubmit(formValues);
            }}
          >
            <Form>
              <FormInput
                keyboardType='email-address'
                returnKeyType='done'
                name='email'
                label='Email'
                accessibilityLabel={
                  accessibilityTags.forgotPasswordEmailFieldID
                }
                testID={accessibilityTags.forgotPasswordEmailFieldID}
                placeholder='Email address'
                autoCapitalize='none'
                autoCorrect={false}
                errorTextColor='dark'
              />
              <View style={{ width: '100%' }}>
                <FormButton
                  accessibilityLabel={accessibilityTags.continueButtonID}
                  testID={accessibilityTags.continueButtonID}
                >
                  Continue
                </FormButton>
                <Spacer size={5} />
                <StyledLink
                  accessibilityLabel={
                    accessibilityTags.forgotPasswordReturnButtonID
                  }
                  testID={accessibilityTags.forgotPasswordReturnButtonID}
                  inverse
                  onPress={() => {
                    navigation.navigate('LoginScreen');
                  }}
                >
                  Return To Sign In
                </StyledLink>
              </View>
            </Form>
          </Formik>
        </StyledFormView>
      </StyledForgotPasswordView>
    </StyledView>
  );
};

export default ForgotPasswordScreen;
